import { createContext, useContext, useState, useEffect } from 'react';
import countries from '../components/countries.json'; // Add this import at the top
const CountryContext = createContext();

export const CountryProvider = ({ children }) => {
  const [selectedCountry, setSelectedCountry] = useState(() => {
    // Try to get from localStorage first
    const saved = localStorage.getItem('selectedCountry');
    return saved ? JSON.parse(saved) : { 
      name: "South Africa", 
      iso: "ZA", 
      code: "27",
      flag: "https://flagcdn.com/w40/za.png",
      regions: {
        "Gauteng": "01",
        "Eastern Cape": "02", 
        "Limpopo": "03",
        "KwaZulu-Natal": "04",
        "Mpumalanga": "05",
        "Western Cape": "06",
        "North West": "07",
        "Northern Cape": "08",
        "Free State": "09"
      }
    };
  });

  const updateCountry = (country) => {
    // Find the full country data including currency
    const fullCountryData = countries.find(c => c.name === country.name) || country;
    
    // Add default regions if not provided
    const countryWithRegions = {
      ...fullCountryData,
      regions: fullCountryData.regions || getDefaultRegions(fullCountryData.name)
    };
    
    setSelectedCountry(countryWithRegions);
    localStorage.setItem('selectedCountry', JSON.stringify(countryWithRegions));
  };

  // Helper function to get default regions for a country
  const getDefaultRegions = (countryName) => {
    const countryMappings = {
      'South Africa': {
        "Gauteng": "01",
        "Eastern Cape": "02",
        "Limpopo": "03",
        "KwaZulu-Natal": "04",
        "Mpumalanga": "05",
        "Western Cape": "06",
        "North West": "07",
        "Northern Cape": "08",
        "Free State": "09"
      },
      'Kenya': {
        "Mombasa": "1",
        "Kwale": "2",
        "Kilifi": "3",
        "Tana River": "4",
        "Lamu": "5",
        "Taita Taveta": "6",
        "Garissa": "7",
        "Wajir": "8",
        "Mandera": "9",
        "Marsabit": "10",
        "Isiolo": "11",
        "Meru": "12",
        "Tharaka-Nithi": "13",
        "Embu": "14",
        "Kitui": "15",
        "Machakos": "16",
        "Makueni": "17",
        "Nyandarua": "18",
        "Nyeri": "19",
        "Kirinyaga": "20",
        "Murang'a": "21",
        "Kiambu": "22",
        "Turkana": "23",
        "West Pokot": "24",
        "Samburu": "25",
        "Trans Nzoia": "26",
        "Uasin Gishu": "27",
        "Elgeyo-Marakwet": "28",
        "Nandi": "29",
        "Baringo": "30",
        "Laikipia": "31",
        "Nakuru": "32",
        "Narok": "33",
        "Kajiado": "34",
        "Kericho": "35",
        "Bomet": "36",
        "Kakamega": "37",
        "Vihiga": "38",
        "Bungoma": "39",
        "Busia": "40",
        "Siaya": "41",
        "Kisumu": "42",
        "Homa Bay": "43",
        "Migori": "44",
        "Kisii": "45",
        "Nyamira": "46",
        "Nairobi City": "47"
      },
      'Namibia': {
        "Erongo": "08",
        "Hardap": "12",
        "Kavango East": "06",
        "Kavango West": "14",
        "Khomas": "11",
        "Kunene": "01",
        "Ohangwena": "04",
        "Omaheke": "10",
        "Omusati": "02",
        "Oshana": "03",
        "Oshikoto": "05",
        "Otjozondjupa": "09",
        "Zambezi": "07",
        "Karas": "013"
      },
      'Zambia': {
        "Western": "1",
        "Central": "2",
        "Eastern": "3",
        "Luapula": "4",
        "Northern": "5",
        "North-Western": "6",
        "Southern": "7",
        "Copperbelt": "8",
        "Lusaka": "9",
        "Muchinga": "10"
      },
      'Burundi': {
        "Buhumuza": "01",
        "Bujumbura": "02",
        "Burunga": "03",
        "Butanyerera": "04",
        "Gitega": "05"
      },
      'Rwanda': {
        "City of Kigali": "01",
        "Eastern": "02",
        "Northern": "03",
        "Southern": "04",
        "Western": "05"
      },
      'Uganda': {
        "Northern": "01",
        "Eastern": "02",
        "Central": "03",
        "Western": "04"
      },
      'Malawi': {
        "Northern": "01",
        "Central": "02",
        "Southern": "03"
      },
      'Angola': {
        "Bengo": "01",
        "Benguela": "02",
        "Bié": "03",
        "Cabinda": "04",
        "Cuando Cubango": "05",
        "Cuanza Norte": "07",
        "Cuanza Sul": "08",
        "Cunene": "09",
        "Huambo": "10",
        "Huíla": "11",
        "Luanda": "13",
        "Lunda Norte": "14",
        "Lunda Sul": "15",
        "Malanje": "16",
        "Moxico": "17",
        "Namibe": "19",
        "Uíge": "20",
        "Zaire": "21"
      },
      'Burkina Faso': {
        "Boucle du Mouhoun": "01",
        "Cascades": "02",
        "Centre": "03",
        "Centre-Est": "04",
        "Centre-Nord": "05",
        "Centre-Ouest": "06",
        "Centre-Sud": "07",
        "Est": "08",
        "Hauts-Bassins": "09",
        "Nord": "10",
        "Plateau-Central": "11",
        "Sahel": "12",
        "Sud-Ouest": "13"
      },
      'Eswatini': {
        "Hhohho": "01",
        "Manzini": "02",
        "Lubombo": "03",
        "Shiselweni": "04"
      },
      'Lesotho': {
        "Berea": "01",
        "Butha-Buthe": "02",
        "Leribe": "03",
        "Mafeteng": "04",
        "Maseru": "05",
        "Mohale's Hoek": "06",
        "Mokhotlong": "07",
        "Qacha's Nek": "08",
        "Quthing": "09",
        "Thaba-Tseka": "10"
      },
      'Tanzania': {
        "Arusha": "01",
        "Dar es Salaam": "02",
        "Dodoma": "03",
        "Geita": "04",
        "Iringa": "05",
        "Kagera": "06",
        "Katavi": "07",
        "Kigoma": "08",
        "Kilimanjaro": "09",
        "Lindi": "10",
        "Manyara": "11",
        "Mara": "12",
        "Mbeya": "13",
        "Mjini Magharibi (Zanzibar Urban/West)": "14",
        "Morogoro": "15",
        "Mtwara": "16",
        "Mwanza": "17",
        "Njombe": "18",
        "Pemba North": "19",
        "Pemba South": "20",
        "Pwani (Coast)": "21",
        "Rukwa": "22",
        "Ruvuma": "23",
        "Shinyanga": "24",
        "Simiyu": "25",
        "Singida": "26",
        "Songwe": "27",
        "Tabora": "28",
        "Tanga": "29"
      },
      'Cameroon': {
        "Adamawa": "01",
        "Centre": "02",
        "East": "03",
        "Far North": "04",
        "Littoral": "05",
        "North": "06",
        "North-West": "07",
        "West": "08",
        "South": "09",
        "South-West": "10"
      },
  'Democratic Republic of the Congo': {
    "Bas-Uele": "01",
    "Equateur": "02",
    "Haut-Katanga": "03",
    "Haut-Lomami": "04",
    "Haut-Uele": "05",
    "Ituri": "06",
    "Kasai": "07",
    "Kasai-Central": "08",
    "Kasai-Oriental": "09",
    "Kinshasa": "10",
    "Kongo-Central": "11",
    "Kwango": "12",
    "Kwilu": "13",
    "Lomami": "14",
    "Lualaba": "15",
    "Mai-Ndombe": "16",
    "Maniema": "17",
    "Mongala": "18",
    "Nord-Kivu": "19",
    "Nord-Ubangi": "20",
    "Sankuru": "21",
    "Sud-Kivu": "22",
    "Sud-Ubangi": "23",
    "Tanganyika": "24",
    "Tshopo": "25",
    "Tshuapa": "26"
  },
  'Congo (Kinshasa/DRC)': { // Add this as an alias
    "Bas-Uele": "01",
    "Equateur": "02",
    "Haut-Katanga": "03",
    "Haut-Lomami": "04",
    "Haut-Uele": "05",
    "Ituri": "06",
    "Kasai": "07",
    "Kasai-Central": "08",
    "Kasai-Oriental": "09",
    "Kinshasa": "10",
    "Kongo-Central": "11",
    "Kwango": "12",
    "Kwilu": "13",
    "Lomami": "14",
    "Lualaba": "15",
    "Mai-Ndombe": "16",
    "Maniema": "17",
    "Mongala": "18",
    "Nord-Kivu": "19",
    "Nord-Ubangi": "20",
    "Sankuru": "21",
    "Sud-Kivu": "22",
    "Sud-Ubangi": "23",
    "Tanganyika": "24",
    "Tshopo": "25",
    "Tshuapa": "26"
  },
      'Ethiopia': {
        "Addis Ababa": "01",
        "Afar": "02",
        "Amhara": "03",
        "Benishangul-Gumuz": "04",
        "Dire Dawa": "05",
        "Gambela": "06",
        "Harari": "07",
        "Oromia": "08",
        "Sidama": "09",
        "Somali": "10",
        "Southern Nations (SNNPR)": "11",
        "Tigray": "12"
      },
    'Côte d\'Ivoire (Ivory Coast)': {
        "Abidjan": "1",
        "Yamoussoukro": "2",
        "Bas-Sassandra": "3",
        "Comoé": "4",
        "Denguélé": "5",
        "Gôh-Djiboua": "6",
        "Lacs": "7",
        "Lagunes": "8",
        "Montagnes": "9",
        "Sassandra-Marahoué": "10",
        "Savanes": "11",
        "Vallée du Bandama": "12",
        "Woroba": "13",
        "Zanzan": "14"
      },
      'Ghana': {
        "WESTERN": "1",
        "CENTRAL": "2",
        "GREATER ACCRA": "3",
        "VOLTA": "4",
        "EASTERN": "5",
        "ASHANTI": "6",
        "WESTERN NORTH": "7",
        "AHAFO": "8",
        "BONO": "9",
        "BONO EAST": "10",
        "OTI": "11",
        "NORTHERN": "12",
        "SAVANNAH": "13",
        "UPPER WEST": "14",
        "NORTH EAST": "15",
        "UPPER EAST": "16"
      },
      'Mozambique': {
        "Cabo Delgado": "1",
        "Gaza": "2",
        "Inhambane": "3",
        "Manica": "4",
        "Maputo City": "5",
        "Maputo": "6",
        "Nampula": "7",
        "Niassa": "8",
        "Sofala": "9",
        "Tete": "10",
        "Zambezia": "11"
      },
      'Nigeria': {
        "North Central (NC)": "1",
        "North East (NE)": "2",
        "North West (NW)": "3",
        "South East (SE)": "4",
        "South South (SS)": "5",
        "South West (SW)": "6"
      },
      // ... other country mappings ...
      'Senegal': {
        "Dakar": "01",
        "Diourbel": "02",
        "Fatick": "03",
        "Kaffrine": "04",
        "Kaolack": "05",
        "Kedougou": "06",
        "Kolda": "07",
        "Louga": "08",
        "Matam": "09",
        "Saint-Louis": "10",
        "Sedhiou": "11",
        "Tambacounda": "12",
        "Thies": "13",
        "Ziguinchor": "14"
      },
    };
    return countryMappings[countryName] || {};
  };

  return (
    <CountryContext.Provider value={{ selectedCountry, setSelectedCountry: updateCountry }}>
      {children}
    </CountryContext.Provider>
  );
};

export const useCountry = () => useContext(CountryContext);